import { CheckCircle, ClipboardList, Loader2, RotateCcw, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { StatusBadge } from "@lexiun/org-admin";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { PageShell, PageHeader } from "../components/layout/PageShell";
import { errorText } from "../lib/task-error";
import type { TaskRecord, TaskStep } from "../types/platform";

function formatStepDuration(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  completed: <CheckCircle className="h-4 w-4 text-green-500" />,
  failed: <XCircle className="h-4 w-4 text-red-500" />,
  running: <Loader2 className="h-4 w-4 animate-spin text-blue-500" />,
  pending: <ClipboardList className="h-4 w-4 text-faint" />,
  rolling_back: <RotateCcw className="h-4 w-4 animate-spin text-yellow-500" />,
};

export function Tasks() {
  const api = usePlatformApi();
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.listTasks({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        limit: 50,
      });
      setTasks(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, typeFilter]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10_000);
    return () => clearInterval(interval);
  }, [load]);

  const handleCancel = async (taskId: string) => {
    setActionLoading(taskId);
    try {
      await api.cancelTask(taskId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to cancel");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = async (taskId: string) => {
    setActionLoading(taskId);
    try {
      await api.retryTask(taskId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to retry");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <PageShell>
      <PageHeader title="Tasks" />

      {/* Filters */}
      <div className="flex gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm dark:border-border dark:bg-panel">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm dark:border-border dark:bg-panel">
          <option value="">All types</option>
          <option value="org-provision">Org Provision</option>
          <option value="user-provision">User Provision</option>
          <option value="user-deprovision">User Deprovision</option>
        </select>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}

      {loading && tasks.length === 0 ? (
        <div className="flex items-center gap-2 py-12 text-sm text-faint">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tasks...
        </div>
      ) : tasks.length === 0 ? (
        <p className="py-12 text-center text-sm text-faint">No tasks found</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <div key={task.taskId} className="rounded-lg border border-border">
              {/* Task header */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedTask(expandedTask === task.taskId ? null : task.taskId)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setExpandedTask(expandedTask === task.taskId ? null : task.taskId); } }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm cursor-pointer"
              >
                {STATUS_ICONS[task.status] ?? STATUS_ICONS.pending}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{task.type}</span>
                    <StatusBadge status={task.status.toUpperCase()} />
                    {task.orgId && <span className="text-xs text-muted-foreground">org: {task.orgId.slice(0, 12)}...</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {task.completedSteps}/{task.totalSteps} steps &middot;
                    {" "}{new Date(task.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {actionLoading === task.taskId ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-faint" />
                  ) : (
                    <>
                      {(task.status === "pending" || task.status === "running") && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleCancel(task.taskId); }}
                          className="rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Cancel"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {task.status === "failed" && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRetry(task.taskId); }}
                          className="rounded p-1 text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="Retry"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Expanded: pipeline stage visualization */}
              {expandedTask === task.taskId && task.steps.length > 0 && (
                <div className="border-t border-border px-4 py-3 dark:border-border">
                  {/* Progress bar */}
                  <div className="mb-3 flex gap-0.5">
                    {task.steps.map((step: TaskStep) => (
                      <div
                        key={step.step}
                        title={`${step.name}: ${step.status}`}
                        className={`h-2 flex-1 rounded-sm ${
                          step.status === "completed" ? "bg-green-500" :
                          step.status === "failed" ? "bg-red-500" :
                          step.status === "running" ? "bg-blue-500 animate-pulse" :
                          "bg-border"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Step details with timing */}
                  <div className="space-y-1.5">
                    {task.steps.map((step: TaskStep, i: number) => (
                      <div key={step.step} className="flex items-center gap-2 text-xs">
                        <span className="w-4 shrink-0 text-right text-[10px] text-faint">{i + 1}</span>
                        {step.status === "completed" ? (
                          <CheckCircle className="h-3 w-3 shrink-0 text-green-500" />
                        ) : step.status === "failed" ? (
                          <XCircle className="h-3 w-3 shrink-0 text-red-500" />
                        ) : step.status === "running" ? (
                          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-500" />
                        ) : (
                          <div className="h-3 w-3 shrink-0 rounded-full border border-border" />
                        )}
                        <span className={`flex-1 ${step.status === "pending" ? "text-faint" : ""}`}>{step.name}</span>
                        {step.startedAt && step.completedAt && (
                          <span className="shrink-0 font-mono text-[10px] text-faint">
                            {formatStepDuration(step.startedAt, step.completedAt)}
                          </span>
                        )}
                        {/* `TaskStep.error` is typed `string` but arrives as an
                            OBJECT. Rendering it raw threw Minified React error
                            #31 and dropped this whole page to "Something went
                            wrong" — in prod, while it was the surface being used
                            to diagnose a provisioning failure (2026-08-12). */}
                        {errorText(step.error) && (
                          <span
                            className="shrink-0 text-red-400 truncate max-w-[200px]"
                            title={errorText(step.error)}
                          >
                            {errorText(step.error)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Task summary */}
                  <div className="mt-2 flex items-center gap-4 text-[10px] text-faint">
                    {task.createdAt && <span>Created: {new Date(task.createdAt).toLocaleString()}</span>}
                    {task.updatedAt && <span>Updated: {new Date(task.updatedAt).toLocaleString()}</span>}
                  </div>

                  {errorText(task.error) && (
                    <div className="mt-2 rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">
                      {errorText(task.error)}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
